import { Target, TrendingUp } from "lucide-react";
import React from "react";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Card, CardContent } from "@/components/ui/card";
import { useApp } from "@/contexts/AppContext";
import { DEMO_CFO_METRICS } from "@/data/demo-data";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";

export default function BoardReadySummaryCard() {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();
  const { metrics: forecastMetrics, isLoading, error } = useBaseForecast();

  if (!hasPermission("view_board_ready_summary_card")) {
    return null;
  }

  if (mode === "live" && isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">
              Loading board summary...
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (mode === "live" && error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-destructive">{error}</div>
        </CardContent>
      </Card>
    );
  }

  if (mode === "live") {
    const groupCash = forecastMetrics?.startingCash ?? 0;
    const daysCashOnHand =
      forecastMetrics && forecastMetrics.avgMonthlyOutflow > 0
        ? Math.round(
            forecastMetrics.startingCash /
              (forecastMetrics.avgMonthlyOutflow / 30),
          )
        : 0;

    return (
      <Card>
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-1">
            <span className="text-lg font-semibold text-neutral-900">
              Board-Ready Summary
            </span>
            <InfoTooltip
              title="Board-Ready Summary"
              content="A high-level view of the four key metrics most commonly requested at board level: group cash position, liquidity ratio, days cash on hand, and cash conversion cycle. All figures are pulled from live data."
            />
          </div>
        </div>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            {/* Metric 1: Cash Position */}
            <div className="space-y-2 p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Target className="h-4 w-4" />
                <span>Cash Position</span>
              </div>
              <div className="text-3xl font-bold">
                {formatCurrency(groupCash)}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: "var(--mc-success-bg)",
                    color: "var(--mc-success-icon)",
                  }}
                >
                  Live
                </span>
                <span className="text-sm text-muted-foreground">
                  vs Last Quarter: N/A
                </span>
              </div>
            </div>

            {/* Metric 2: Liquidity Ratio */}
            <div className="space-y-2 p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Target className="h-4 w-4" />
                <span>Liquidity Ratio</span>
              </div>
              <div className="text-3xl font-bold text-muted-foreground">
                N/A
              </div>
              <div className="text-sm text-muted-foreground">
                Historical data not yet available
              </div>
            </div>

            {/* Metric 3: Days Cash on Hand */}
            <div className="space-y-2 p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Target className="h-4 w-4" />
                <span>Days Cash on Hand</span>
              </div>
              <div className="text-3xl font-bold">{daysCashOnHand} days</div>
              <div className="text-sm text-muted-foreground">
                Industry average not yet available
              </div>
            </div>

            {/* Metric 4: Cash Conversion Cycle */}
            <div className="space-y-2 p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <TrendingUp className="h-4 w-4" />
                <span>Cash Conversion Cycle</span>
              </div>
              <div className="text-3xl font-bold text-muted-foreground">
                N/A
              </div>
              <div className="text-sm text-muted-foreground">
                Historical data not yet available
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Demo mode
  const metrics = DEMO_CFO_METRICS;
  const cashVsLastQuarter = metrics.groupCash - metrics.lastQuarterCash;
  const cashGrowthPercent = (
    (cashVsLastQuarter / metrics.lastQuarterCash) *
    100
  ).toFixed(1);
  const daysCashVsIndustry = (
    (metrics.daysCashOnHand / metrics.industryAvgDaysCash) * 100 -
    100
  ).toFixed(0);
  const cashConversionImprovement =
    metrics.lastQuarterCashConversion - metrics.cashConversionCycle;

  return (
    <Card>
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-1">
          <span className="text-lg font-semibold text-neutral-900">
            Board-Ready Summary
          </span>
          <InfoTooltip
            title="Board-Ready Summary"
            content="A high-level view of the four key metrics most commonly requested at board level: group cash position, liquidity ratio, days cash on hand, and cash conversion cycle. All figures are pulled from live data."
          />
        </div>
      </div>
      <CardContent>
        <div className="grid grid-cols-2 gap-6">
          {/* Metric 1: Cash Position */}
          <div className="space-y-2 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-base text-neutral-900">
              <Target className="h-4 w-4" />
              <span>Cash Position</span>
            </div>
            <div className="text-3xl font-bold">
              {formatCurrency(metrics.groupCash)}
            </div>
            <div className="flex items-center gap-2">
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: "var(--mc-success-bg)",
                  color: "var(--mc-success-icon)",
                }}
              >
                Healthy
              </span>
              <span className="text-sm text-muted-foreground">
                vs Last Quarter: +{formatCurrency(cashVsLastQuarter)} (+
                {cashGrowthPercent}%)
              </span>
            </div>
          </div>

          {/* Metric 2: Liquidity Ratio */}
          <div className="space-y-2 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-base text-neutral-900">
              <Target className="h-4 w-4" />
              <span>Liquidity Ratio</span>
            </div>
            <div className="text-3xl font-bold">
              {metrics.liquidityRatio.toFixed(1)}x
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Target: &gt;1.5x
              </span>

              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingUp
                  className="h-3 w-3"
                  style={{ color: "var(--mc-success-icon)" }}
                />
                Improving
              </span>
            </div>
          </div>

          {/* Metric 3: Days Cash on Hand */}
          <div className="space-y-2 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-base text-neutral-900">
              <Target className="h-4 w-4" />
              <span>Days Cash on Hand</span>
            </div>
            <div className="text-3xl font-bold">
              {metrics.daysCashOnHand} days
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Industry Avg: {metrics.industryAvgDaysCash} days
              </span>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: "var(--mc-success-bg)",
                  color: "var(--mc-success-icon)",
                }}
              >
                +{daysCashVsIndustry}% above average
              </span>
            </div>
          </div>

          {/* Metric 4: Cash Conversion Cycle */}
          <div className="space-y-2 p-4 rounded-lg bg-muted/50">
            <div className="flex items-center gap-2 text-base text-neutral-900">
              <TrendingUp className="h-4 w-4" />
              <span>Cash Conversion Cycle</span>
            </div>
            <div className="text-3xl font-bold">
              {metrics.cashConversionCycle} days
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Last Quarter: {metrics.lastQuarterCashConversion} days
              </span>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: "var(--mc-error-bg)",
                  color: "var(--mc-error-icon)",
                }}
              >
                -{cashConversionImprovement} days
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
